/**
 * THE INSTALL CONTROL — the one place the Owner can ask for Scout on the home
 * screen.
 *
 * WHAT IT RENDERS IS DECIDED ENTIRELY BY WHAT THE BROWSER SAID, and that is the
 * whole design. `installCapability()` answers one of four facts about THIS
 * browser, and each gets a different rendering because each calls for a
 * different action from the Owner:
 *
 *   installed   → a statement, and NO control. Offering to install something
 *                 that is installed is the duplicate prompt the brief forbids.
 *   promptable  → ONE button, which raises the browser's own prompt. This is
 *                 the only state where a one-tap install exists.
 *   manual      → the instructions for THIS browser's own menu. Honest, because
 *                 no page can perform that flow for the Owner.
 *   unsupported → a statement, and no control. There is nothing to offer.
 *
 * THE OUTCOME IS THE BROWSER'S, REPORTED VERBATIM. An accepted install says the
 * browser is installing; a dismissed one says it was dismissed. Nothing here
 * reports success because a prompt was displayed — that is the false "installed"
 * message the brief names, and it is a single boolean away at all times.
 */

import { useState } from 'preact/hooks';
import { installCapability, installRevision, requestInstall } from '../install';

export function InstallControl() {
	// READ THE REVISION SO THE CAPABILITY IS RE-EVALUATED when the browser's
	// answer changes — the deferred prompt arriving, or the app being installed
	// while this page is open. Without this the control would render once, at
	// mount, which is the state a browser is least likely to still be in.
	void installRevision();
	const capability = installCapability();
	const [outcome, setOutcome] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);

	async function install(): Promise<void> {
		setBusy(true);
		const result = await requestInstall();
		setBusy(false);
		// EACH OUTCOME SAID AS ITSELF. `unavailable` is not a failure and is not
		// a dismissal: it means the browser had already withdrawn its offer, and
		// the honest sentence sends the Owner to the menu that still works.
		setOutcome(
			result.kind === 'accepted'
				? 'The browser is installing Scout. It will appear on your home screen when it finishes.'
				: result.kind === 'dismissed'
					? 'The install was dismissed, so nothing was installed. Scout keeps working in this tab, and you can ask again.'
					: 'The browser is no longer offering a one-tap install. Its own menu still has one — see the note above.',
		);
	}

	return (
		<section class="install" aria-labelledby="install-heading">
			<h2 class="subhead" id="install-heading">
				Install Scout
			</h2>

			{capability.kind === 'installed' && <p class="install-detail">{capability.detail}</p>}

			{capability.kind === 'unsupported' && <p class="install-detail">{capability.detail}</p>}

			{capability.kind === 'manual' && <p class="install-detail">{capability.detail}</p>}

			{capability.kind === 'promptable' && (
				<>
					<p class="install-detail">
						Scout can be installed as an application: its own icon, its own window, and the Main Chat
						when it opens. Nothing is downloaded and no store is involved.
					</p>
					<button type="button" class="button" disabled={busy} onClick={() => void install()}>
						{busy ? 'Asking the browser…' : 'Install Scout'}
					</button>
				</>
			)}

			{outcome !== null && (
				<p class="install-detail" role="status">
					{outcome}
				</p>
			)}
		</section>
	);
}
